import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal data-fetching hook.
 *
 * No query library: the console's needs are a load, a reload and an error state, and a
 * dependency that big would outweigh what it saves here.
 */
export interface AsyncState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);

    loaderRef
      .current()
      .then((result) => {
        // Discards the result of a request whose component already unmounted or whose
        // dependencies changed — otherwise a slow response overwrites a fresh one.
        if (active) setData(result);
      })
      .catch((caught: unknown) => {
        if (active) setError(caught);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { data, error, loading, reload };
}

/** Runs an action, tracking its in-flight state and error. */
export function useAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
): {
  run: (...args: Args) => Promise<Result | undefined>;
  pending: boolean;
  error: unknown;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const run = useCallback(
    async (...args: Args) => {
      setPending(true);
      setError(undefined);
      try {
        return await action(...args);
      } catch (caught) {
        setError(caught);
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [action],
  );

  return { run, pending, error };
}

/** Re-renders on an interval; used for "há 24 segundos" style labels (PRD §26). */
export function useTicker(intervalMs = 15_000): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
}
